import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * ArrowUp / ArrowDown / Enter / Escape navigation for suggestion lists.
 * @param {object} options
 * @param {boolean} options.isOpen
 * @param {number} options.itemCount
 * @param {(index: number) => void} options.onSelectIndex
 * @param {() => void} [options.onClose]
 * @param {unknown} [options.resetKey] - reset highlight when this changes (e.g. search term)
 */
export function useListKeyboardNav({
  isOpen,
  itemCount,
  onSelectIndex,
  onClose,
  resetKey,
}) {
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const itemRefs = useRef([]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [resetKey, isOpen, itemCount]);

  useEffect(() => {
    if (!isOpen || itemCount === 0) return;
    const el = itemRefs.current[highlightedIndex];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, isOpen, itemCount]);

  const setItemRef = useCallback((index, node) => {
    itemRefs.current[index] = node;
  }, []);

  const onKeyDown = useCallback(
    (event) => {
      if (!isOpen || itemCount <= 0) {
        if (event.key === 'Escape' && onClose) {
          event.preventDefault();
          onClose();
        }
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex((prev) => (prev < itemCount - 1 ? prev + 1 : 0));
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : itemCount - 1));
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        const index = Math.min(Math.max(highlightedIndex, 0), itemCount - 1);
        onSelectIndex(index);
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        if (onClose) onClose();
      }
    },
    [highlightedIndex, isOpen, itemCount, onClose, onSelectIndex]
  );

  return {
    highlightedIndex,
    setHighlightedIndex,
    onKeyDown,
    setItemRef,
  };
}

export default useListKeyboardNav;
